import { useEffect } from 'react';
import { Undo2, Redo2 } from 'lucide-react';

interface UndoRedoControlsProps {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  lastActionDescription?: string;
  nextRedoDescription?: string;
}

export default function UndoRedoControls({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  lastActionDescription,
  nextRedoDescription,
}: UndoRedoControlsProps) {
  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        if (e.shiftKey) {
          // Cmd/Ctrl + Shift + Z = Redo
          e.preventDefault();
          if (canRedo) onRedo();
        } else {
          // Cmd/Ctrl + Z = Undo
          e.preventDefault();
          if (canUndo) onUndo();
        }
      }
      // Alternative: Cmd/Ctrl + Y = Redo
      if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        e.preventDefault();
        if (canRedo) onRedo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [canUndo, canRedo, onUndo, onRedo]);

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={onUndo}
        disabled={!canUndo}
        title={lastActionDescription ? `Undo: ${lastActionDescription}` : 'Nothing to undo'}
        className={`p-1.5 rounded-md border transition-colors ${
          canUndo
            ? 'border-[var(--border-default)] bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]'
            : 'border-transparent bg-transparent text-[var(--text-muted)] cursor-not-allowed'
        }`}
      >
        <Undo2 size={18} />
      </button>
      <button
        onClick={onRedo}
        disabled={!canRedo}
        title={nextRedoDescription ? `Redo: ${nextRedoDescription}` : 'Nothing to redo'}
        className={`p-1.5 rounded-md border transition-colors ${
          canRedo
            ? 'border-[var(--border-default)] bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]'
            : 'border-transparent bg-transparent text-[var(--text-muted)] cursor-not-allowed'
        }`}
      >
        <Redo2 size={18} />
      </button>
    </div>
  );
}
